import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import { formatEUR } from '../utils/format';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const { clearCart } = useCartStore();

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else if (orderId) {
      // Direct order success (bank transfer)
      fetchOrderDetails();
    } else {
      setStatus('error');
    }
  }, [sessionId, orderId]);

  const verifyPayment = async () => {
    try {
      // Check payment status with Stripe
      const response = await axiosInstance.get(`/stripe/checkout-status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();

        // Fetch order details using session_id (works for guests too)
        try {
          const orderResp = await axiosInstance.get(`/orders/by-session/${sessionId}`);
          setOrderDetails(orderResp.data);
        } catch (err) {
          // Fallback: try the authenticated endpoint if we have an order_id
          if (orderId) {
            fetchOrderDetails();
          }
        }
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await axiosInstance.get(`/orders/my-orders/${orderId}`);
      setOrderDetails(response.data);
      setStatus('success');
      clearCart();
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#C4A084] mx-auto mb-4" />
          <p className="text-lg text-gray-600">Zahlung wird überprüft...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#E8E0D5' }}>
        <Card className="max-w-md w-full rounded-none">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Zahlung fehlgeschlagen</h1>
            <p className="text-gray-600 mb-6">
              Die Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/checkout')}
                className="w-full rounded-none"
                style={{ backgroundColor: '#C4A084' }}
              >
                Erneut versuchen
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full rounded-none"
              >
                Zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-2xl mx-auto">
        <Card className="rounded-none">
          <CardContent className="pt-8 pb-8">
            {/* Success Header */}
            <div className="text-center mb-8">
              <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Vielen Dank für Ihre Bestellung!
              </h1>
              <p className="text-gray-600">
                {orderDetails && orderDetails.payment_status !== 'paid'
                  ? 'Ihre Bestellung ist eingegangen.'
                  : 'Ihre Zahlung wurde erfolgreich verarbeitet.'}
              </p>
            </div>

            {/* Order Details */}
            {orderDetails && (
              <div className="border-t border-b py-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-600">Bestellnummer:</span>
                  <span className="font-bold text-[#5D4E3C]">
                    {orderDetails.order_number || orderDetails.id?.substring(0, 8).toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-600">Gesamtbetrag:</span>
                  <span className="font-bold text-[#5D4E3C]">
                    {formatEUR(orderDetails.total)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status:</span>
                  {orderDetails.payment_status === 'paid' ? (
                    <span data-testid="order-payment-status" className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-none">
                      Bezahlt
                    </span>
                  ) : (
                    <span data-testid="order-payment-status" className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-none">
                      Zahlung ausstehend
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-[#F5EDE4] p-4 rounded-none mb-6">
              <div className="flex items-start gap-3">
                <Package className="h-5 w-5 text-[#C4A084] mt-0.5" />
                <div>
                  <p className="font-medium text-[#5D4E3C]">Was passiert als Nächstes?</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Sie erhalten in Kürze eine Bestätigungs-E-Mail mit allen Details zu Ihrer Bestellung.
                    Wir bereiten Ihre Bestellung vor und informieren Sie, sobald sie versendet wird.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Link to="/my-orders">
                <Button 
                  className="w-full rounded-none"
                  style={{ backgroundColor: '#C4A084' }}
                >
                  Meine Bestellungen anzeigen
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/products">
                <Button 
                  variant="outline"
                  className="w-full rounded-none border-[#C4A084] text-[#C4A084]"
                >
                  Weiter einkaufen
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderSuccess;
